import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/termini")({
  head: () => ({
    meta: [
      { title: "Termini e Condizioni — ChefConnect Italia" },
      {
        name: "description",
        content:
          "Termini e condizioni d'uso di ChefConnect Italia: ruoli, account, contenuti, abbonamenti Premium, responsabilità e legge applicabile.",
      },
      { property: "og:title", content: "Termini e Condizioni — ChefConnect Italia" },
      {
        property: "og:description",
        content:
          "Le regole di utilizzo della piattaforma per clienti e chef.",
      },
      { property: "og:url", content: "https://chefconnectitalia.com/termini" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/termini" }],
  }),
  component: TerminiPage,
});

function TerminiPage() {
  return (
    <article className="container mx-auto max-w-3xl px-4 py-16 sm:py-24">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Legale</p>
      <h1 className="mt-3 font-display text-5xl">Termini e Condizioni</h1>
      <p className="mt-3 text-sm text-muted-foreground">Ultimo aggiornamento: 27 giugno 2026</p>

      <div className="mt-10 space-y-10 text-[15px] leading-relaxed text-muted-foreground">
        <p>
          I presenti Termini e Condizioni ("Termini") regolano l'utilizzo del
          sito{" "}
          <a href="https://chefconnectitalia.com" className="text-foreground hover:text-gold">
            chefconnectitalia.com
          </a>{" "}
          ("ChefConnect" o "la Piattaforma"). Utilizzando ChefConnect accetti
          integralmente i Termini.
        </p>

        <Section title="1. Natura del servizio">
          <p>
            ChefConnect è una piattaforma tecnologica che mette in contatto
            clienti privati e chef professionisti per servizi di cucina a
            domicilio (cene private, show cooking, eventi). ChefConnect{" "}
            <strong className="text-foreground">non è parte</strong> del
            rapporto contrattuale che si instaura tra cliente e chef, non
            organizza il servizio, non determina prezzi e non risponde della
            qualità della prestazione.
          </p>
        </Section>

        <Section title="2. Account e registrazione">
          <ul className="list-disc space-y-2 pl-5">
            <li>La registrazione è gratuita e richiede un'email valida.</li>
            <li>L'utente deve avere almeno 18 anni.</li>
            <li>
              È vietato creare account con dati falsi, impersonare altre persone
              o usare la piattaforma per scopi illeciti.
            </li>
            <li>
              L'utente è responsabile della custodia delle credenziali e di ogni
              attività compiuta dal proprio account.
            </li>
          </ul>
        </Section>

        <Section title="3. Obblighi degli chef">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Possedere i requisiti professionali, fiscali e sanitari richiesti
              dalla normativa italiana per la propria attività (es. P.IVA,
              attestato HACCP ove applicabile).
            </li>
            <li>
              ChefConnect è una piattaforma di intermediazione tra clienti e chef.
              Ogni chef è responsabile del rispetto degli obblighi fiscali, amministrativi
              e professionali applicabili alla propria attività. ChefConnect non verifica
              la posizione fiscale degli utenti.
            </li>
            <li>Fornire informazioni veritiere e foto effettivamente proprie.</li>
            <li>
              Emettere fattura o ricevuta fiscale al cliente nei casi previsti
              dalla legge.
            </li>
            <li>
              Rispondere autonomamente della qualità del servizio, della
              sicurezza alimentare e del rispetto degli accordi presi col cliente.
            </li>
          </ul>
        </Section>

        <Section title="4. Obblighi dei clienti">
          <ul className="list-disc space-y-2 pl-5">
            <li>Comunicare con gli chef in modo corretto e rispettoso.</li>
            <li>
              Concordare direttamente con lo chef prezzo, menu, data, numero
              ospiti, allergie, modalità di pagamento ed eventuali cancellazioni.
            </li>
            <li>Non utilizzare la piattaforma per finalità diverse da quelle previste.</li>
          </ul>
        </Section>

        <Section title="5. Contenuti pubblicati">
          <p>
            L'utente è l'unico responsabile dei contenuti caricati (foto, video,
            testi, recensioni). Concedendoli a ChefConnect, l'utente garantisce
            di possederne i diritti e concede a ChefConnect una licenza non
            esclusiva, gratuita e limitata al funzionamento della Piattaforma
            per pubblicarli e mostrarli. ChefConnect può rimuovere contenuti
            che violino la legge, i Termini o i diritti di terzi.
          </p>
        </Section>

        <Section title="6. Abbonamenti Premium">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Premium è un servizio opzionale a pagamento riservato agli chef.
              Le funzionalità incluse sono descritte nella pagina{" "}
              <Link to="/premium" className="text-foreground hover:text-gold">
                Premium
              </Link>
              .
            </li>
            <li>
              I pagamenti sono gestiti tramite Stripe; ChefConnect non memorizza
              i dati di pagamento sui propri server.
            </li>
            <li>
              L'abbonamento è rinnovato automaticamente alla scadenza, salvo
              disdetta dalla dashboard.
            </li>
            <li>
              Il cliente consumatore può esercitare il diritto di recesso entro
              14 giorni, salvo che il servizio sia già stato integralmente
              fruito con consenso esplicito (art. 59 Codice del Consumo).
            </li>
            <li>
              Offerta di lancio: i primi 100 chef ricevono 30 giorni di Premium gratis
              + badge Founder permanente, senza obbligo di rinnovo.
            </li>
          </ul>
        </Section>

        <Section title="7. Limitazione di responsabilità">
          <p>
            ChefConnect fornisce la Piattaforma "così com'è" e si impegna a
            mantenerla operativa con la massima diligenza, senza tuttavia
            garantire l'assenza di interruzioni o errori. ChefConnect non
            risponde dei danni derivanti dall'esecuzione del servizio di cucina
            (intossicazioni, allergie, danni a cose, ritardi, qualità delle
            pietanze) né delle controversie tra cliente e chef.
          </p>
        </Section>

        <Section title="8. Sospensione e cancellazione dell'account">
          <p>
            ChefConnect può sospendere o chiudere account che violino i Termini,
            la legge o i diritti di terzi, dandone comunicazione via email
            quando possibile. L'utente può cancellare il proprio account in
            qualsiasi momento dalla dashboard o scrivendo a{" "}
            <a href="mailto:informazioni@chefconnectitalia.com" className="text-foreground hover:text-gold">
              informazioni@chefconnectitalia.com
            </a>
            .
          </p>
        </Section>

        <Section title="9. Modifiche ai Termini">
          <p>
            Possiamo aggiornare i presenti Termini per riflettere evoluzioni
            normative o di prodotto. Le modifiche sostanziali saranno comunicate
            via email o all'interno della Piattaforma con un preavviso ragionevole.
          </p>
        </Section>

        <Section title="10. Legge applicabile e foro competente">
          <p>
            I Termini sono regolati dalla legge italiana. Per ogni controversia
            è competente in via esclusiva il foro del luogo di residenza o
            domicilio del consumatore, ove applicabile; in tutti gli altri casi
            il foro di riferimento del Titolare.
          </p>
        </Section>
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl text-foreground">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}